import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import { useTranslation } from "common/locales";
import {
  ConfirmCovers,
  EnterCovers,
  EnterReasons,
  EnterTime,
} from "features/bulkCoverInput";
import { StyledInputContainer } from "features/StyledInputContainer";
import {
  deleteAllTilesForField,
  Field,
  invalidateFieldData,
  ManualDMInputItem,
  RootState,
  selectAllFields,
  useAppDispatch,
} from "model";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import { createManyReadings, EventType } from "../../model/manualInputSlice";

function roundToHour(date: Date) {
  const p = 60 * 60 * 1000; // milliseconds in an hour
  return new Date(Math.round(date.getTime() / p) * p);
}

export default function Covers() {
  const { path } = useRouteMatch();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentFarmId = useSelector(
    (state: RootState) => state.farms.currentFarmId
  );

  const handleClose = () => {
    history.push(`/farm/${currentFarmId || ""}`);
  };

  const fields = useSelector(selectAllFields);

  const [manualDMInputs, setManualDMInputs] = useState<
    Array<ManualDMInputItem>
  >([]);
  const [dateTimeMeasurementStart, setDateTimeMeasurementStart] = useState(
    roundToHour(new Date())
  );
  const [dateTimeMeasurementEnd, setDateTimeMeasurementEnd] = useState(
    roundToHour(new Date())
  );

  useEffect(() => {
    if (fields.length > 0) {
      setManualDMInputs(
        fields.map((field: Field) => ({
          dryMatterValue: field.dryMatterNow,
          field: field.guid,
          fieldName: field.name,
          event: { eventType: EventType.None },
          kind: "user",
          verificationStatus: "Accepted",
        }))
      );
    }
  }, [fields]);

  async function handleSave() {
    const preparedInputs = manualDMInputs.reduce(
      (acc: Array<ManualDMInputItem>, manualInput) => {
        if (manualInput.newDryMatterValue) {
          acc.push({
            ...manualInput,
            dryMatterValue: manualInput.newDryMatterValue,
          });
        }
        return acc;
      },
      []
    );
    await dispatch(
      createManyReadings(
        preparedInputs.map((i) => ({
          guid: "new",
          manualDryMatterInput: {
            ...i,
            dateTimeMeasurementStart: dateTimeMeasurementStart.toISOString(),
            dateTimeMeasurementEnd: dateTimeMeasurementEnd.toISOString(),
            event: {
              ...i.event,
              dateTimeCut:
                i.event.dateTimeCut && i.event.dateTimeCut.toISOString(),
              dateTimeIn:
                i.event.dateTimeIn && i.event.dateTimeIn.toISOString(),
              dateTimeOut:
                i.event.dateTimeOut && i.event.dateTimeOut.toISOString(),
            },
          },
        }))
      )
    ).then(unwrapResult);
    manualDMInputs
      .filter((i) => i.newDryMatterValue)
      .forEach(async (i) => {
        // it's important to wait for the tiles to be deleted,
        // as we are setting field.tiles=[] when we are invalidating field data,
        // and we need the field to still have tile ids when deleting the tiles for a field
        await dispatch(deleteAllTilesForField(i.field));
        dispatch(invalidateFieldData(i.field));
      });
    notify(t("addCovers.success"));
    mixpanel.track("CoverAdd successful");
    history.push(`/farm/${currentFarmId || ""}`);
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <Switch>
          <Route path={`${path}/enter-time`}>
            <EnterTime
              dateTimeMeasurementStart={dateTimeMeasurementStart}
              setDateTimeMeasurementStart={setDateTimeMeasurementStart}
              dateTimeMeasurementEnd={dateTimeMeasurementEnd}
              setDateTimeMeasurementEnd={setDateTimeMeasurementEnd}
            />
          </Route>
          <Route path={`${path}/enter-covers`}>
            <EnterCovers
              manualDMInputs={manualDMInputs}
              setManualDMInputs={setManualDMInputs}
            />
          </Route>
          <Route path={`${path}/enter-reasons`}>
            <EnterReasons
              manualDMInputs={manualDMInputs}
              setManualDMInputs={setManualDMInputs}
            />
          </Route>
          <Route path={`${path}/confirm-covers`}>
            <ConfirmCovers
              dateTimeMeasurementStart={dateTimeMeasurementStart}
              dateTimeMeasurementEnd={dateTimeMeasurementEnd}
              manualDMInputs={manualDMInputs}
              handleSave={handleSave}
            />
          </Route>
        </Switch>
      </StyledInputContainer>
    </Dialog>
  );
}
